import { useEffect, useState } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";

import { ArtifactView } from "@/components/assistant-ui/artifact-view";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { RequireAuth } from "@/components/route-guards";
import { downloadArtifact, useArtifactResource } from "@/lib/artifact-resource";

/**
 * `/artifacts/$artifactId` -- one generated file filling the window.
 *
 * Where `window.open` lands from the side panel's pop-out. A real route rather than a written
 * document, because people treat a popped-out window like a window: it survives reload, can be
 * bookmarked, and re-presigns its own URL from its own session when the five minutes are up.
 *
 * Takes nothing from its opener and reads no query string -- the id in the path is the whole
 * input. Filename, content type and the two markers come from the download-url route, checked
 * against the caller's entitlement on every open. A viewer trusting its opener for the content
 * type would render whatever it was told and lose it on reload.
 *
 * `RequireAuth` for the signed-out case: a window reopened from a bookmark should start
 * sign-in, not render an empty frame.
 */
export const Route = createFileRoute("/artifacts/$artifactId")({
  component: ArtifactRoute,
});

function ArtifactRoute() {
  return (
    <RequireAuth>
      <ArtifactWindow />
    </RequireAuth>
  );
}

function ArtifactWindow() {
  const { artifactId } = useParams({ from: "/artifacts/$artifactId" });
  const resource = useArtifactResource({ artifactId });
  const [downloadFailed, setDownloadFailed] = useState(false);

  const title = resource.filename || "Artifact";

  // The title bar is the only label a popped-out window has. Left alone until the filename is
  // known, so it never flashes the word "Artifact".
  useEffect(() => {
    if (resource.filename) document.title = resource.filename;
  }, [resource.filename]);

  return (
    <div className="bg-background flex h-screen min-h-0 flex-col">
      <header
        data-slot="artifact-window-header"
        className="flex h-12 shrink-0 items-center gap-1 border-b px-3"
      >
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={title}>
          {title}
        </span>
        <TooltipIconButton
          tooltip="Download"
          disabled={!resource.url}
          onClick={() => {
            if (!resource.url) return;
            setDownloadFailed(false);
            downloadArtifact(resource.url, title).catch(() => setDownloadFailed(true));
          }}
          data-slot="artifact-window-download"
        >
          <DownloadIcon className="size-4" />
        </TooltipIconButton>
      </header>
      {downloadFailed && (
        <p className="text-destructive shrink-0 px-3 py-1.5 text-xs">
          The download failed. Try again.
        </p>
      )}
      <ArtifactView resource={resource} />
    </div>
  );
}
